export interface CoostoQueryValidation {
  valid: boolean
  message?: CoostoQueryError
}

export type CoostoQueryError =
  | 'empty'
  | 'unclosedQuote'
  | 'emptyPhrase'
  | 'unsupportedSyntax'
  | 'lowercaseOperator'
  | 'invalidStructure'

type Token =
  | { type: 'term'; value: string; quoted: boolean }
  | { type: 'operator'; value: 'AND' | 'OR' }
  | { type: 'leftParen' }
  | { type: 'rightParen' }

function tokenize(query: string): Token[] | CoostoQueryError {
  const tokens: Token[] = []
  let index = 0

  while (index < query.length) {
    if (/\s/.test(query[index])) {
      index += 1
      continue
    }

    if (query[index] === '(') {
      tokens.push({ type: 'leftParen' })
      index += 1
      continue
    }
    if (query[index] === ')') {
      tokens.push({ type: 'rightParen' })
      index += 1
      continue
    }
    if (query[index] === '"') {
      const end = query.indexOf('"', index + 1)
      if (end === -1) return 'unclosedQuote'
      if (end === index + 1) return 'emptyPhrase'
      tokens.push({ type: 'term', value: query.slice(index + 1, end), quoted: true })
      index = end + 1
      continue
    }

    const start = index
    while (index < query.length && !/[\s()"]/.test(query[index])) index += 1
    const value = query.slice(start, index)
    if (!value || value.includes('*') || value.includes(':') || value.startsWith('-')) {
      return 'unsupportedSyntax'
    }
    if (value === 'AND' || value === 'OR') {
      tokens.push({ type: 'operator', value })
    } else if (value === 'NOT' || value === 'and' || value === 'or' || value === 'not') {
      return 'lowercaseOperator'
    } else {
      tokens.push({ type: 'term', value, quoted: false })
    }
  }

  return tokens
}

class QueryParser {
  private index = 0

  private readonly tokens: Token[]

  constructor(tokens: Token[]) {
    this.tokens = tokens
  }

  parse(): boolean {
    if (!this.parseOperand()) return false

    while (this.peek()?.type === 'operator') {
      this.index += 1
      if (!this.parseOperand()) return false
    }

    return true
  }

  atEnd(): boolean {
    return this.index === this.tokens.length
  }

  private parseOperand(): boolean {
    const token = this.peek()
    if (!token) return false

    if (token.type === 'term') {
      this.index += 1
      // Adjacent unquoted words remain separate terms; they are not an exact phrase.
      while (this.peek()?.type === 'term' && !(this.peek() as Extract<Token, { type: 'term' }>).quoted) {
        this.index += 1
      }
      return true
    }

    if (token.type !== 'leftParen') return false
    this.index += 1
    if (!this.parse()) return false
    if (this.peek()?.type !== 'rightParen') return false
    this.index += 1
    return true
  }

  private peek(): Token | undefined {
    return this.tokens[this.index]
  }
}

export function validateCoostoQuery(query: string): CoostoQueryValidation {
  const trimmed = query.trim()
  if (!trimmed) return { valid: false, message: 'empty' }

  const tokenized = tokenize(trimmed)
  if (typeof tokenized === 'string') return { valid: false, message: tokenized }

  const parser = new QueryParser(tokenized)
  if (!parser.parse() || !parser.atEnd()) {
    return { valid: false, message: 'invalidStructure' }
  }

  return { valid: true }
}
